import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, User, Users } from "lucide-react";
import { useFormQuestionsData } from "@/hooks/useFormRelationships";
import { MatrixRowSelector } from "@/components/MatrixRowSelector";
import { HierarchicalGroupSelector } from "./HierarchicalGroupSelector";
import { useCalculations } from "@/hooks/useCalculations";

// Tipo de valor que puede ser nuevo formato (objeto) o antiguo formato (número/string directo)
type ValueFormat =
  | { type: "number" | "form_variable" | "calculation"; value: string }
  | number
  | string;

// Función para normalizar valores del formato antiguo al nuevo
function normalizeValue(value: ValueFormat): { type: string; value: string } {
  if (typeof value === "object" && value !== null && "type" in value) {
    // Ya está en el formato nuevo
    return value;
  }
  // Formato antiguo: convertir a formato nuevo
  return {
    type: "number",
    value: String(value || ""),
  };
}

// Función para obtener el tipo y valor de un ValueFormat
function getValueParts(value: ValueFormat): { type: string; value: string } {
  return normalizeValue(value);
}

interface DataParam {
  // Para gráficos comunes: un solo valor
  value: ValueFormat;
  label?: string;
  // Para bar-referral: segundo valor (referralValue)
  value2?: ValueFormat;
  // Campos para form_variable
  formSource?: "main" | "referral";
  formSource2?: "main" | "referral";
  // Para preguntas de matriz - valor usuario
  matrixRowId?: string;
  matrixRowText?: string;
  matrixColumnId?: string;
  matrixColumnText?: string;
  // Para preguntas de matriz - valor referido
  matrixRowId2?: string;
  matrixRowText2?: string;
  matrixColumnId2?: string;
  matrixColumnText2?: string;
  // Para selección jerárquica de grupos
  selectedGroup?: string;
  selectedQuestion?: string;
  selectedGroup2?: string;
  selectedQuestion2?: string;
}

interface ChartDataParamsProps {
  chartType: string;
  dataParams: DataParam[];
  onChange: (params: DataParam[]) => void;
  labels: Record<string, string>;
  onLabelsChange: (labels: Record<string, string>) => void;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
  templateId: number;
}

// Configuración de parámetros según tipo de gráfico
const CHART_PARAM_CONFIGS: Record<string, { count: number; description: string }> = {
  bar: { count: -1, description: "Agrega tantos valores como barras necesites" }, // Variable count
  pie: { count: 3, description: "3 valores: Manager, Messenger, Mentor" },
  radial: { count: -1, description: "Valores por cada dimensión en cada categoría" },
  "pie-only-color": { count: 3, description: "3 valores para el gráfico" },
  "bar-referral": { count: -1, description: "Pares de valores: usuario y referido por cada barra" },
  "pie-update": { count: 4, description: "4 valores para los sectores" },
};

export function ChartDataParams({
  chartType,
  dataParams,
  onChange,
  labels,
  onLabelsChange,
  jotformFormId,
  jotformFormIdReferral,
  templateId
}: ChartDataParamsProps) {
  const config = CHART_PARAM_CONFIGS[chartType] || { count: 1, description: "Valores del gráfico" };
  const isVariableCount = config.count === -1;

  // Hook para obtener datos de ambos formularios
  const mainFormData = useFormQuestionsData(jotformFormId);
  const referralFormData = useFormQuestionsData(jotformFormIdReferral);

  // Hook para obtener cálculos disponibles
  const { calculations } = useCalculations(templateId);

  // Combinar los datos de ambos formularios (si existen)
  const formsData: any[] = [];
  if (Array.isArray(mainFormData.formsData)) {
    formsData.push(...mainFormData.formsData.map((form: any) => ({ ...form, isReferral: false })));
  }
  if (Array.isArray(referralFormData.formsData)) {
    formsData.push(...referralFormData.formsData.map((form: any) => ({ ...form, isReferral: true })));
  }

  // IDs de formularios seleccionados por fuente
  const mainFormId = formsData.find(f => !f.isReferral)?.formId || jotformFormId || "";
  const referralFormId = formsData.find(f => f.isReferral)?.formId || jotformFormIdReferral || "";

  // Function to check if a question is a matrix question
  const isMatrixQuestion = (questionName: string, formSource: "main" | "referral" = "main") => {
    const currentFormData = formSource === "main" ? mainFormData : referralFormData;
    const formsData = currentFormData.formsData || [];
    
    const targetForm = formsData.find(form => 
      formSource === "main" ? !form.isReferral : form.isReferral
    );

    if (!targetForm || !targetForm.questions) {
      return false;
    }

    const question = targetForm.questions.find(
      (q: any) => q.name === questionName || q.qid === questionName
    );

    return question?.type === 'control_matrix';
  };

  const handleParamChange = (index: number, field: keyof DataParam | 'value.type' | 'value.value' | 'value2.type' | 'value2.value', value: any) => {
    const newParams = [...dataParams];
    if (!newParams[index]) {
      newParams[index] = {
        value: { type: "number", value: "" },
        label: ""
      } as any;
    }
    const param = newParams[index];

    if (field === 'value.type' || field === 'value.value') {
      const [parent, child] = field.split('.');
      if (typeof param.value === 'object' && param.value !== null && 'type' in param.value) {
        param.value[child as 'type' | 'value'] = value;
      }
    } else if (field === 'value2.type' || field === 'value2.value') {
      const [parent, child] = field.split('.');
      if (typeof param.value2 === 'object' && param.value2 !== null && 'type' in param.value2) {
        param.value2[child as 'type' | 'value'] = value;
      }
    } else {
      (param as any)[field] = value;
    }

    onChange(newParams);
  };

  const handleLabelChange = (field: string, value: string) => {
    onLabelsChange({ ...labels, [field]: value });
  };

  const addParam = () => {
    console.log('🔍 Adding parameter, current params:', dataParams.length);
    const newParams = [...dataParams];
    
    // Si hay parámetros anteriores, copiar el último (sin la etiqueta para evitar duplicados)
    if (dataParams.length > 0) {
      const lastParam = dataParams[dataParams.length - 1];
      const copiedParam = {
        ...lastParam,
        label: "", // Reset label to avoid duplicates
        // Mantener formSource pero resetear campos jerárquicos
        selectedGroup: "",
        selectedQuestion: "",
        matrixRowId: "",
        matrixColumnId: "",
        matrixRowText: "",
        matrixColumnText: "",
        // Para bar-referral, también copiar value2 y formSource2 pero resetear sus campos jerárquicos
        ...(chartType === "bar-referral" && lastParam.value2 ? {
          value2: lastParam.value2,
          selectedGroup2: "",
          selectedQuestion2: "",
          matrixRowId2: "",
          matrixColumnId2: "",
          matrixRowText2: "",
          matrixColumnText2: ""
        } : {})
      };
      newParams.push(copiedParam);
      console.log('🔍 Copied last parameter:', copiedParam);
    } else {
      // Si no hay parámetros anteriores, crear uno nuevo
      newParams.push({ 
        value: { type: "number", value: "" }, 
        label: "" 
      });
      console.log('🔍 Created new parameter from scratch');
    }
    
    console.log('🔍 New params after adding:', newParams.length);
    onChange(newParams);
  };

  const removeParam = (index: number) => {
    const newParams = dataParams.filter((_, i) => i !== index);
    onChange(newParams);
  };

  // Initialize params if needed
  if (!isVariableCount && dataParams.length < config.count) {
    const newParams = [...dataParams];
    while (newParams.length < config.count) {
      newParams.push({ 
        value: { type: "number", value: "" }, 
        label: "" 
      });
    }
    onChange(newParams);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{config.description}</p>

      {/* Chart-specific labels */}
      {chartType === "bar-referral" && (
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="space-y-2">
            <Label htmlFor="userLabel">Etiqueta Usuario</Label>
            <Input
              id="userLabel"
              value={labels.userLabel || ""}
              onChange={(e) => handleLabelChange("userLabel", e.target.value)}
              placeholder="Ej: Usuario"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="referralLabel">Etiqueta Referido</Label>
            <Input
              id="referralLabel"
              value={labels.referralLabel || ""}
              onChange={(e) => handleLabelChange("referralLabel", e.target.value)}
              placeholder="Ej: Referido"
            />
          </div>
        </div>
      )}

      {/* Data parameters */}
      <div className="space-y-3">
        {(() => {
          console.log('🔍 Rendering params:', dataParams.length, dataParams);
          return null;
        })()}
        {dataParams.map((param, index) => {
          const valueParts = getValueParts(param.value);
          const value2Parts = param.value2 ? getValueParts(param.value2) : null;

          return (
          <div key={index} className="flex gap-2 items-end p-3 border rounded-lg">
            <div className="flex-1 space-y-2">
                {/* Parámetro 1 - Valor Usuario */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Valor Usuario</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                      value={valueParts.type}
                      onValueChange={(value) => {
                        console.log('🔍 Value type changed:', value);
                        handleParamChange(index, "value.type", value);
                        // Reset value when changing type
                        if (value === "number") {
                          handleParamChange(index, "value.value", "");
                        }
                      }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="number">Número</SelectItem>
                            <SelectItem value="form_variable">Variable Formulario</SelectItem>
                            <SelectItem value="calculation">Cálculo</SelectItem>
                          </SelectContent>
                        </Select>
                    {valueParts.type === "form_variable" ? (
                      <div className="space-y-2">
                          <Select
                            value={param.formSource || "main"}
                            onValueChange={(value) => handleParamChange(index, "formSource", value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="main">👤 Valor usuario (Formulario Principal)</SelectItem>
                              <SelectItem value="referral">👥 Valor referido (Formulario de Referidos)</SelectItem>
                            </SelectContent>
                          </Select>
                        <div className="space-y-2">
                          <HierarchicalGroupSelector
                            selectedGroup={param.selectedGroup || ''}
                            selectedQuestion={param.selectedQuestion || ''}
                            onGroupChange={(groupPath) => {
                              handleParamChange(index, "selectedGroup", groupPath);
                              // Reset question when group changes
                              handleParamChange(index, "selectedQuestion", "");
                              handleParamChange(index, "value.value", "");
                              handleParamChange(index, "matrixRowId", "");
                              handleParamChange(index, "matrixColumnId", "");
                            }}
                            onQuestionChange={(questionName, questionText) => {
                              handleParamChange(index, "selectedQuestion", questionName);
                              handleParamChange(index, "value.value", `answers.${questionName}`);
                            }}
                            jotformFormId={param.formSource === "referral" ? jotformFormIdReferral : jotformFormId}
                            showQuestionSelector={true}
                            questionSelectorType="select"
                            className="text-sm"
                          />

                      {/* Matrix Row and Column Selection - Valor Usuario */}
                          {param.selectedQuestion && isMatrixQuestion(param.selectedQuestion, param.formSource || "main") && (
                            <div className="mt-2">
                          <MatrixRowSelector
                                questionName={param.selectedQuestion}
                            jotformFormId={param.formSource === "referral" ? jotformFormIdReferral || '' : jotformFormId || ''}
                            selectedRowId={param.matrixRowId}
                            selectedColumnId={param.matrixColumnId}
                            onRowChange={(rowId, rowText) => {
                                  handleParamChange(index, "matrixRowId", rowId);
                                  handleParamChange(index, "matrixRowText", rowText);
                            }}
                            onColumnChange={(columnId, columnText) => {
                                  handleParamChange(index, "matrixColumnId", columnId);
                                  handleParamChange(index, "matrixColumnText", columnText);
                            }}
                            showColumns={true}
                          />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : valueParts.type === "calculation" ? (
                      <Select
                        value={valueParts.value}
                        onValueChange={(value) => handleParamChange(index, "value.value", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cálculo" />
                        </SelectTrigger>
                        <SelectContent>
                          {calculations.map((calc) => (
                            <SelectItem key={calc.id} value={calc.name}>
                              {calc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={valueParts.value}
                        onChange={(e) => handleParamChange(index, "value.value", e.target.value)}
                        placeholder="Valor numérico"
                      />
                    )}
                  </div>
                    </div>

                {/* Parámetro 2 - Valor Referido (solo para bar-referral) */}
                {chartType === "bar-referral" && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Valor Referido</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                        value={value2Parts?.type || "number"}
                        onValueChange={(value) => {
                          console.log('🔍 Value2 type changed:', value);
                          handleParamChange(index, "value2.type", value);
                          // Reset value when changing type
                          if (value === "number") {
                            handleParamChange(index, "value2.value", "");
                          } else if (value === "form_variable") {
                            // Initialize value2 if it doesn't exist
                            if (!param.value2) {
                              handleParamChange(index, "value2", { type: "form_variable", value: "" });
                            }
                            // Reset form variable fields
                            handleParamChange(index, "selectedGroup2", "");
                            handleParamChange(index, "selectedQuestion2", "");
                            handleParamChange(index, "value2.value", "");
                          } else if (value === "calculation") {
                            // Initialize value2 if it doesn't exist
                            if (!param.value2) {
                              handleParamChange(index, "value2", { type: "calculation", value: "" });
                            }
                            // Reset calculation fields
                            handleParamChange(index, "value2.value", "");
                          }
                        }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="number">Número</SelectItem>
                            <SelectItem value="form_variable">Variable Formulario</SelectItem>
                            <SelectItem value="calculation">Cálculo</SelectItem>
                          </SelectContent>
                        </Select>
                      {(() => {
                        console.log('🔍 Value2 debug:', { 
                          value2Parts, 
                          type: value2Parts?.type, 
                          isFormVariable: value2Parts?.type === "form_variable" 
                        });
                        return value2Parts?.type === "form_variable";
                      })() ? (
                        <div className="space-y-2">
                          <Select
                            value={param.formSource2 || "main"}
                            onValueChange={(value) => handleParamChange(index, "formSource2", value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="main">👤 Valor usuario (Formulario Principal)</SelectItem>
                              <SelectItem value="referral">👥 Valor referido (Formulario de Referidos)</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="space-y-2">
                            <HierarchicalGroupSelector
                              selectedGroup={param.selectedGroup2 || ''}
                              selectedQuestion={param.selectedQuestion2 || ''}
                              onGroupChange={(groupPath) => {
                                handleParamChange(index, "selectedGroup2", groupPath);
                                // Reset question when group changes
                                handleParamChange(index, "selectedQuestion2", "");
                                handleParamChange(index, "value2.value", "");
                                // Reset matrix fields for value2
                                handleParamChange(index, "matrixRowId2", "");
                                handleParamChange(index, "matrixColumnId2", "");
                              }}
                              onQuestionChange={(questionName, questionText) => {
                                console.log('🔍 Value2 question selected:', questionName);
                                handleParamChange(index, "selectedQuestion2", questionName);
                                handleParamChange(index, "value2.value", `answers.${questionName}`);
                              }}
                              jotformFormId={param.formSource2 === "referral" ? jotformFormIdReferral : jotformFormId}
                              showQuestionSelector={true}
                              questionSelectorType="select"
                              className="text-sm"
                            />

                            {/* Matrix Row and Column Selection - Valor Referido */}
                            {param.selectedQuestion2 && isMatrixQuestion(param.selectedQuestion2, param.formSource2 || "main") && (
                              <div className="mt-2">
                                <MatrixRowSelector
                                  questionName={param.selectedQuestion2}
                                  jotformFormId={param.formSource2 === "referral" ? jotformFormIdReferral || '' : jotformFormId || ''}
                                  selectedRowId={param.matrixRowId2}
                                  selectedColumnId={param.matrixColumnId2}
                                  onRowChange={(rowId, rowText) => {
                                    handleParamChange(index, "matrixRowId2", rowId);
                                    handleParamChange(index, "matrixRowText2", rowText);
                                  }}
                                  onColumnChange={(columnId, columnText) => {
                                    handleParamChange(index, "matrixColumnId2", columnId);
                                    handleParamChange(index, "matrixColumnText2", columnText);
                                  }}
                                  showColumns={true}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : value2Parts?.type === "calculation" ? (
                        <Select
                          value={value2Parts.value}
                          onValueChange={(value) => handleParamChange(index, "value2.value", value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar cálculo" />
                          </SelectTrigger>
                          <SelectContent>
                            {calculations.map((calc) => (
                              <SelectItem key={calc.id} value={calc.name}>
                                {calc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={value2Parts?.value || ""}
                          onChange={(e) => handleParamChange(index, "value2.value", e.target.value)}
                          placeholder="Valor numérico"
                        />
                      )}
                    </div>
                  </div>
                )}

                    {/* Etiqueta */}
                    <Input
                      value={param.label || ""}
                      onChange={(e) => handleParamChange(index, "label", e.target.value)}
                      placeholder="Etiqueta (opcional)"
                    />
                  </div>

              {/* Botón eliminar */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeParam(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
          </div>
          );
        })}
      </div>

      {/* Botón agregar parámetro */}
      {isVariableCount && (
        <Button onClick={addParam} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {dataParams.length > 0 ? "Agregar Parámetro (Copia del anterior)" : "Agregar Parámetro"}
        </Button>
      )}

      {/* Información de tipos */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>• <strong>Número:</strong> Valor numérico directo (ej: 7.5)</p>
        <p>• <strong>Variable Formulario:</strong> Selecciona una pregunta específica del formulario</p>
        <p>• <strong>Cálculo:</strong> Expresión de cálculo o nombre de variable calculada</p>
      </div>
    </div>
  );
}